// ServiceList.tsx
import React, { useState, useMemo } from "react";
// Asegúrate de que las interfaces Service y ServiceType sean importadas correctamente
import { Service, ServiceType } from "../interfaces/types";
import { useParams, useNavigate } from "react-router-dom";

interface ServiceListProps {
  onAddService: () => void;
  // NUEVAS PROPS para recibir los datos de la API
  services: Service[];
  serviceTypes: ServiceType[];
}

const ServiceList: React.FC<ServiceListProps> = ({
  onAddService,
  // Desestructuramos las nuevas props
  services,
  serviceTypes,
}) => {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");

  // Mapeo para los nombres de las categorías en el filtro
  // *** USAMOS serviceTypes DE LAS PROPS ***

  // Lógica de filtrado
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const typeMatch =
        typeFilter === "all" || service.categorySlug === typeFilter;
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "active" && service.status) ||
        (statusFilter === "inactive" && !service.status);
      return typeMatch && statusMatch;
    });
  }, [typeFilter, statusFilter, services]);

  function formatCurrencyMXN(amountString: string): string {
    // 1. Convert the string to a number. If it's not a valid number, it will be NaN.
    const amount: number = parseFloat(amountString);

    // Basic validation
    if (isNaN(amount)) {
      return "Invalid amount";
    }

    // 2. Use Intl.NumberFormat for professional currency formatting.
    // 'es-MX' (Spanish - Mexico) locale ensures correct formatting conventions.
    const formatter = new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN", // Mexican Peso
      minimumFractionDigits: 0, // Set to 0 to remove cents (e.g., 300.00 -> $300)
      maximumFractionDigits: 0,
    });

    // The default output will be "$300", so we append " MXN" to match your desired output "$300MXN".
    const formattedAmount = formatter.format(amount);

    // Remove the space before "MXN"
    return formattedAmount.replace(" ", "") + " MXN";
  }
  function formatMinutesToHours(totalMinutes: number): string {
    // Basic validation
    if (isNaN(totalMinutes) || totalMinutes < 0) {
      return "Input must be a non-negative number.";
    }

    // 1. Calculate hours and remaining minutes.
    const hours: number = Math.floor(totalMinutes / 60);
    const minutes: number = totalMinutes % 60;

    // 2. Build the output string.

    let parts: string[] = [];

    // Format the hours part
    if (hours > 0) {
      const hourUnit = hours === 1 ? "hora" : "horas";
      parts.push(`${hours} ${hourUnit}`);
    }

    // Format the minutes part
    if (minutes > 0) {
      const minuteUnit = minutes === 1 ? "minuto" : "minutos";
      parts.push(`${minutes} ${minuteUnit}`);
    }

    // Handle the case where the input is 0 or less than 1 minute
    if (parts.length === 0) {
      return "0 minutos";
    }

    // Join the parts with " y " (and)
    return parts.join(" y ");
  }

  // Función para renderizar una tarjeta de servicio
  const renderServiceCard = (service: Service) => {
    const isActive = service.status;
    const statusText = isActive ? "Activo" : "Inactivo";
    const statusClass = isActive
      ? "status-badge--active"
      : "status-badge--inactive";
    const activateButton = !isActive ? (
      <button className="btn btn--success">Activar</button>
    ) : (
      <></>
    );

    return (
      <div
        className="service-card"
        data-category={service.categorySlug}
        data-status={service.status}
        key={service.id}
      >
        <div className="service-card__header">
          <h3 className="service-card__title">{service.name}</h3>
          <span className="service-card__category">
            {service.service_type?.name
              ? service.service_type?.name
              : "Sin tipo de servicio"}
          </span>
        </div>
        <div className="service-card__body">
          <p className="service-card__description">{service.description}</p>
          <div className="service-card__details">
            <div className="service-card__detail">
              <i className="fas fa-clock service-card__detail-icon icon-primary"></i>
              <span>{formatMinutesToHours(service.duration_minutes)}</span>
            </div>
            {/* <div className="service-card__detail">
              <i className="fas fa-user service-card__detail-icon icon-primary"></i>
              <span>{service.mode}</span>
            </div> */}
          </div>
          <div className="service-card__price">
            {formatCurrencyMXN(service.price)}
          </div>
        </div>
        <div className="service-card__footer">
          <div className="service-card__status">
            <span className={`status-badge ${statusClass}`}>
              ● {statusText}
            </span>
          </div>
          <div className="service-card__actions">
            <button
              className="btn btn--secondary"
              onClick={() => navigate(`/service/${service.id}`)}
            >
              Editar
            </button>
            {activateButton}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="filters" id="services-filters">
        {/* <div className="filter-group">
          <label htmlFor="service-type-filter">Filtrar por Tipo</label>
        </div> */}
        <div className="filter-group">
          <label htmlFor="service-status-filter">Filtrar por Estado</label>
          <select
            id="service-status-filter"
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as "all" | "active" | "inactive")
            }
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
        <div className="filter-group">
          <label>&nbsp;</label>
          <button className="btn btn--primary" onClick={onAddService}>
            <i className="fas fa-plus"></i> Agregar Servicio
          </button>
        </div>
      </div>

      <div className="services-grid" id="services-container">
        {filteredServices.length > 0 ? (
          filteredServices.map(renderServiceCard)
        ) : (
          <div className="empty-state">
            <i className="fas fa-search-minus empty-state__icon"></i>
            <h3>No se encontraron servicios</h3>
            <p>Ajusta los filtros o agrega un nuevo servicio.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default ServiceList;
